import axiosInstance from '@/setup/axios/axiosInstance'
import {EmployeeModel, EmployeeResponse, CreateEmployeeRequest} from '../models/Employee'

// Get all employees
export interface EmployeeFilters {
  search?: string;
  company?: string;
  department?: string;
  role?: string;
  status?: string;
  attendance_type?: string;
  page?: number;
  page_size?: number;
  no_pagination?: boolean;
  timekeeping?: boolean;
  date?: string;
  timesheet?: boolean;
  month?: number;
  year?: number;
  exclude?: string;
  mission?: string;
}

export interface AttendanceBoardFilters extends EmployeeFilters {
  date__gte?: string;
  date__lte?: string;
}

export interface AttendanceBoardWindowItem {
  date: string;
  display_date?: string;
  has_timekeeping?: boolean;
  timekeeping?: any;
}

export interface AttendanceBoardEmployee extends Partial<EmployeeModel> {
  id: string;
  name: string;
  code: string;
  attendance_window?: AttendanceBoardWindowItem[];
}

export interface AttendanceBoardResponse {
  attendance_range?: {
    date__gte?: string;
    date__lte?: string;
    total_days?: number;
  };
  results: AttendanceBoardEmployee[];
}

export function getEmployees(filters?: EmployeeFilters, config?: { signal?: AbortSignal }) {
  const params = new URLSearchParams();
  if (filters?.search && filters.search.trim()) {
    params.append('search', filters.search.trim());
  }
  if (filters?.company && filters.company !== 'all' && filters.company !== 'global') {
    params.append('company', filters.company);
  }
  if (filters?.department && filters.department !== 'all') {
    params.append('department', filters.department);
  }
  if (filters?.role && filters.role !== 'all') {
    params.append('role', filters.role);
  }
  if (filters?.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }
  if (filters?.attendance_type) {
    params.append('attendance_type', filters.attendance_type);
  }
  if (filters?.page !== undefined) {
    params.append('page', String(filters.page));
  }
  if (filters?.page_size !== undefined) {
    params.append('page_size', String(filters.page_size));
  }
  if (filters?.no_pagination !== undefined) {
    params.append('no_pagination', String(filters.no_pagination));
  }
  if (filters?.timekeeping !== undefined) {
    params.append('timekeeping', String(filters.timekeeping));
  }
  if (filters?.date) {
    params.append('date', filters.date);
  }
  if (filters?.timesheet !== undefined) {
    params.append('timesheet', String(filters.timesheet));
  }
  if (filters?.month !== undefined) {
    params.append('month', String(filters.month));
  }
  if (filters?.year !== undefined) {
    params.append('year', String(filters.year));
  }
  if (filters?.exclude !== undefined) {
    params.append('exclude', filters.exclude);
  }
  if (filters?.mission) {
    params.append('mission', filters.mission);
  }
  const queryString = params.toString();
  return axiosInstance.get<EmployeeResponse>(`/employees/${queryString ? `?${queryString}` : ''}`, config)
}

export function getAttendanceBoard(filters?: AttendanceBoardFilters, config?: { signal?: AbortSignal }) {
  const params = new URLSearchParams();
  if (filters?.search && filters.search.trim()) {
    params.append('search', filters.search.trim());
  }
  if (filters?.company && filters.company !== 'all' && filters.company !== 'global') {
    params.append('company', filters.company);
  }
  if (filters?.department && filters.department !== 'all') {
    params.append('department', filters.department);
  }
  if (filters?.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }
  if (filters?.attendance_type) {
    params.append('attendance_type', filters.attendance_type);
  }
  if (filters?.date__gte) {
    params.append('date__gte', filters.date__gte);
  }
  if (filters?.date__lte) {
    params.append('date__lte', filters.date__lte);
  }
  if (filters?.no_pagination !== undefined) {
    params.append('no_pagination', String(filters.no_pagination));
  }

  const queryString = params.toString();
  return axiosInstance.get<AttendanceBoardResponse>(
    `/employees/attendance-board/${queryString ? `?${queryString}` : ''}`,
    config,
  );
}

// Get single employee by ID
export function getEmployee(id: string) {
  return axiosInstance.get<EmployeeModel>(`/employees/${id}/`)
}

// Create new employee with user
export function createEmployeeWithUser(employeeData: CreateEmployeeRequest) {
  return axiosInstance.post<EmployeeModel>('/employees/create_with_user/', employeeData)
}

// Update employee
export function updateEmployee(id: string, employeeData: Partial<EmployeeModel>) {
  return axiosInstance.put<EmployeeModel>(`/employees/${id}/`, employeeData)
}

// Delete employee
export function deleteEmployee(id: string) {
  return axiosInstance.delete(`/employees/${id}/`)
}

export type AvailableUsersQuery = {
  company?: string;
  search?: string;
};

// Get available users (users without employee profiles)
export function getAvailableUsers(query?: AvailableUsersQuery | string) {
  const normalized: AvailableUsersQuery =
    typeof query === 'string' ? { company: query } : (query ?? {});

  const params = new URLSearchParams();
  const company = normalized.company?.trim();
  const search = normalized.search?.trim();

  if (company && company !== 'all' && company !== 'global') {
    params.append('company', company);
  }
  if (search) {
    params.append('search', search);
  }

  const queryString = params.toString();
  return axiosInstance.get(
    queryString ? `/employees/available_users/?${queryString}` : '/employees/available_users/'
  );
}

// Get employee by user ID
export function getEmployeeByUser(userId: string) {
  return axiosInstance.get<EmployeeModel>(`/employees/by_user/?user_id=${userId}`, {
    // Don't log 404 errors as they're expected when user is not an employee
    validateStatus: (status) => status < 500
  })
}

// Upload an employee photo to the media backend (S3) and return its URL
export const uploadEmployeeImage = async (
  file: File,
  field: 'image1' | 'image2' = 'image1'
): Promise<{ field: string; url: string; path: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('field', field);
  const response = await axiosInstance.post('/employees/upload-image/', formData);
  return response.data;
};

// Soft delete employees
export const softDeleteEmployees = async (employeeIds: string[]) => {
  try {
    const response = await axiosInstance.post("/employees/soft_delete/", {
      employee_ids: employeeIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Restore deleted employees
export const restoreEmployees = async (employeeIds: string[]) => {
  try {
    const response = await axiosInstance.post("/employees/restore_deleted/", {
      employee_ids: employeeIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Permanently delete employees
export const permanentDeleteEmployees = async (employeeIds: string[]) => {
  try {
    const response = await axiosInstance.post("/employees/permanent_delete/", {
      employee_ids: employeeIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export type GetDeletedEmployeesQuery = {
  company?: string;
  search?: string;
};

// Get deleted employees (soft-deleted only)
export const getDeletedEmployees = async (query?: GetDeletedEmployeesQuery) => {
  try {
    const params = new URLSearchParams();
    params.append('show_deleted', 'true');
    params.append('no_pagination', 'true');

    const company = query?.company?.trim();
    if (company && company !== 'all' && company !== 'global') {
      params.append('company', company);
    }

    const search = query?.search?.trim();
    if (search) {
      params.append('search', search);
    }

    const response = await axiosInstance.get(`/employees/?${params.toString()}`);
    return response;
  } catch (error) {
    throw error;
  }
};

export interface EmployeeImportRowIssue {
  row: number;
  code?: string;
  reason?: string;
  field?: string;
  message?: string;
}

export interface EmployeeImportResult {
  created: number;
  skipped: EmployeeImportRowIssue[];
  errors: EmployeeImportRowIssue[];
  warnings: EmployeeImportRowIssue[];
}

// Import employees from an .xlsx file into one explicit company
export function importEmployees(file: File, company: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('company', company);
  return axiosInstance.post<EmployeeImportResult>('/employees/import/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export interface EmployeeExportFilters {
  company?: string;
}

// Export employees to Excel (scoped by company; omit company for all/global)
export function exportEmployees(filters?: EmployeeExportFilters) {
  const params = new URLSearchParams();
  const company = filters?.company?.trim();
  if (company && company !== 'all' && company !== 'global') {
    params.append('company', company);
  }
  const queryString = params.toString();
  return axiosInstance.get(
    `/employees/export/${queryString ? `?${queryString}` : ''}`,
    { responseType: 'blob' }
  );
} 
